<template>
  <v-data-table
    :headers="headers"
    :items="allocations"
    :items-per-page="-1"
    :loading="is_loading"
    hide-default-footer
    show-expand
    single-expand
    :expanded.sync="expanded"
    @click:row="toggleRow"
  >
    <template v-slot:item.id="{ item }">
      <v-chip
        small
      >
        {{ item.id }}
      </v-chip>
    </template>
    <template v-slot:item.status="{ item }">
      <v-chip
        v-if="hasOpenAllocations(item)"
        small
        color="info"
        dark
      >
        <v-icon left small>
          mdi-clock-outline
        </v-icon>
        Offen
      </v-chip>
    </template>
    <template v-slot:item.active_count="{ item }">
      <v-chip small>
        {{ item.active_count }}
      </v-chip>
    </template>
    <template v-slot:item.allocations="{ item }">
      <v-chip
        small
      >
        {{ item.allocations.length }}
      </v-chip>
    </template>
    <template v-slot:item.user_actions="{ item }">
      <v-btn icon small @click.stop="openEdit(item)">
        <v-icon small>mdi-pencil</v-icon>
      </v-btn>
      <v-tooltip bottom :disabled="canDelete(item)">
        <template v-slot:activator="{ on, attrs }">
          <div class="d-inline-block" v-bind="attrs" v-on="on">
            <v-btn
              icon
              small
              :disabled="!canDelete(item)"
              @click.stop="openDelete(item)"
            >
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </template>
        <span>Erst alle Artikel einziehen</span>
      </v-tooltip>
    </template>
    <template v-slot:expanded-item="{ headers, item }">
      <td :colspan="headers.length" class="pa-0">
        <v-container
          fluid
        >
          <v-row>
            <v-col cols="12">
              <v-data-table
                :headers="allocationHeaders"
                :items="sortedAllocations(item.allocations)"
                dense
                :items-per-page="-1"
                hide-default-footer
              >
                <template v-slot:item.state="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        small
                        dark
                        :color="stateMeta(item.state).color"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon left small>
                          {{ stateMeta(item.state).icon }}
                        </v-icon>
                        {{ stateMeta(item.state).label }}
                      </v-chip>
                    </template>
                    <span>
                      Letzer Statuswechsel:
                      {{ formatTimestamp(item.state_timestamp) }}
                      {{ item.state_editor ? (" von "+ item.state_editor) : "" }}
                    </span>
                  </v-tooltip>
                </template>
                <template v-slot:item.created_at="{ item }">
                  {{ formatTimestamp(item.created_at )}}
                </template>
                <template v-slot:item.sku="{ item }">
                  <v-chip small>
                    {{ variantMap[item.variant_id]?.variant.sku || "-" }}
                  </v-chip>
                </template>
                <template v-slot:item.product_name="{ item }">
                    {{ variantMap[item.variant_id]?.product.name || "-" }}
                </template>
                <template v-slot:item.variant_name="{ item }">
                    {{ variantMap[item.variant_id]?.variant.size || "-" }}
                </template>
                <template v-slot:item.comment="{ item }">
                  <v-tooltip bottom v-if="item.comment">
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        class="text-truncate d-inline-block"
                        style="max-width: 200px;"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ item.comment }}
                      </span>
                    </template>
                    <span>{{ item.comment }}</span>
                  </v-tooltip>

                  <span v-else>-</span>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    v-if="item.state === 1"
                    x-small
                    color="success"
                    @click="setState(item, 3)"
                  >
                    Aushändigen
                  </v-btn>
                  <v-btn
                    v-if="item.state === 1"
                    class="ml-2"
                    x-small
                    color="error"
                    @click="setState(item, 2)"
                  >
                    Ablehnen
                  </v-btn>
                  <v-btn
                    v-if="item.state === 3"
                    x-small
                    color="info"
                    @click="setState(item, 4)"
                  >
                    Einziehen
                  </v-btn>
                  <v-btn
                    v-if="item.state === 3"
                    class="ml-2"
                    x-small
                    color="warning"
                    @click="setState(item, 5)"
                  >
                    Vernichten
                  </v-btn>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-container>
      </td>
    </template>
    <template v-slot:top>
      <v-dialog v-model="editDialog" max-width="480" @click:outside="closeEdit">
        <v-card v-if="editing">
          <v-card-title>Person bearbeiten</v-card-title>
          <v-card-text>
            <v-form ref="editForm" v-model="editValid">
              <v-text-field
                v-model="editForm.last_name"
                label="Name"
                :rules="[editRules.required]"
                outlined
                dense
              ></v-text-field>
              <v-text-field
                v-model="editForm.email"
                label="E-Mail"
                :rules="[editRules.required, editRules.email]"
                :error-messages="emailDuplicate ? ['E-Mail wird bereits verwendet'] : []"
                outlined
                dense
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text :disabled="saving" @click="closeEdit">Abbrechen</v-btn>
            <v-btn
              color="primary"
              :disabled="!canSave"
              :loading="saving"
              @click="saveEdit"
            >
              Speichern
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="deleteDialog" max-width="480" @click:outside="closeDelete">
        <v-card v-if="deleting">
          <v-card-title>Person löschen</v-card-title>
          <v-card-text>
            <p>
              <strong>{{ deleting.first_name }} {{ deleting.last_name }}</strong>
              wird aus der Liste entfernt.
              {{ deleting.allocations.length }} Artikel in der Historie bleiben
              in der Datenbank erhalten.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text :disabled="deleteSaving" @click="closeDelete">Abbrechen</v-btn>
            <v-btn color="error" :loading="deleteSaving" @click="confirmDelete">
              Löschen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </v-data-table>
</template>

<script>

export default {
  name: "ShopAllocationManagement",
  props: ["activeTab"],
  watch: {
    activeTab(newVal) {
      if(newVal === 2) {
        this.updateAllocations();
      }
    }
  },
  data: () => ({
    expanded: [],
    spinning: {},
    headers: [
      { text: "Status", value: "status", sortable: false },
      { text: "Name", value: "last_name" },
      { text: "E-Mail", value: "email" },
      { text: "Ausgegebene Artikel", value: "active_count" },
      { text: "Artikel Insgesamt", value: "allocations" },
      { text: "", value: "user_actions", sortable: false, width: 100 },
    ],
    editDialog: false,
    editing: null,
    editValid: false,
    saving: false,
    editForm: { last_name: "", email: "" },
    editRules: {
      required: (v) => (!!v && String(v).trim().length > 0) || "Pflichtfeld",
      email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || "") || "Ungültige E-Mail",
    },
    deleteDialog: false,
    deleting: null,
    deleteSaving: false,
    allocationHeaders: [
      { text: "Status", value: "state", sortable: false },
      { text: "Bestelldatum", value: "created_at", sortable: false },
      { text: "SKU", value: "sku", sortable: false },
      { text: "Produkt", value: "product_name", sortable: false },
      { text: "Variante", value: "variant_name", sortable: false },
      { text: "Anmerkung", value: "comment", sortable: false },
      { text: "Aktionen", value: "actions", sortable: false }
    ]
  }),
  methods: {
    setState(allocation, newState) {
      allocation.state = 9
      this.$store.dispatch("update", { payload_type: "PUT", context: "/shop/allocations/" + allocation.id + "/state?new_state=" + newState });
    },
    sortedAllocations(list) {
      if (!Array.isArray(list)) return [];
      const priority = {
        1: 0, // Offen ganz oben
        3: 1  // Ausgegeben danach
      };
      return [...list].sort((a, b) => {
        const aPriority = priority[a.state] ?? 2;
        const bPriority = priority[b.state] ?? 2;

        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        return new Date(b.state_timestamp) - new Date(a.state_timestamp);
      });
    },
    formatTimestamp(ts) {
      if (!ts) return "-";

      const date = new Date(ts);

      return date.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    hasOpenAllocations(user) {
      return Array.isArray(user.allocations) &&
        user.allocations.some(a => a.state === 1);
    },
    stateMeta(state) {
      const map = {
        1: { label: "Offen", color: "blue", icon: "mdi-clock-outline" },
        2: { label: "Abgelehnt", color: "grey", icon: "mdi-close-circle" },
        3: { label: "Ausgegeben", color: "green", icon: "mdi-check-circle" },
        4: { label: "Zurückgegeben", color: "grey", icon: "mdi-undo" },
        5: { label: "Vernichtet", color: "grey", icon: "mdi-delete" }
      }
      return map[state] || { label: "Unbekannt", color: "grey", icon: "mdi-help-circle" }
    },
    getProduct(productId) {
      return this.$store.state.shop.products.find(
        p => p.id === productId
      );
    },
    getVariant(productId, variantId) {
      const product = this.getProduct(productId);
      if (!product) return null;

      return product.variants.find(v => v.id === variantId);
    },
    toggleRow(item) {
      const index = this.expanded.findIndex(i => i.id === item.id)
      if (index === -1) {
        this.expanded = [item] // wegen single-expand
      } else {
        this.expanded = []
      }
    },
    updateAllocations(){
      this.$store.dispatch("update", { context: "/shop/allocations" });
    },
    openEdit(item) {
      this.editing = item;
      this.editForm = {
        last_name: item.last_name || "",
        email: item.email || "",
      };
      this.editDialog = true;
      this.$nextTick(() => {
        if (this.$refs.editForm) this.$refs.editForm.resetValidation();
      });
    },
    closeEdit() {
      if (this.saving) return;
      this.editDialog = false;
      this.editing = null;
    },
    async saveEdit() {
      if (!this.editing || !this.canSave) return;
      const person = this.editing;
      const newLastName = this.editForm.last_name.trim();
      const newEmail = this.editForm.email.trim();
      const nameChanged = newLastName !== (person.last_name || "");
      const emailChanged = newEmail !== (person.email || "");

      this.saving = true;
      try {
        if (nameChanged) {
          await this.$store.dispatch("update", {
            payload_type: "PUT",
            context: "/shop/persons/" + person.id + "/name",
            payload: {
              first_name: person.first_name || "",
              last_name: newLastName,
            },
          });
          person.last_name = newLastName;
        }
        if (emailChanged) {
          await this.$store.dispatch("update", {
            payload_type: "PUT",
            context: "/shop/persons/" + person.id + "/email",
            payload: { email: newEmail },
          });
          person.email = newEmail;
        }
      } finally {
        this.saving = false;
        this.editDialog = false;
        this.editing = null;
      }
    },
    canDelete(user) {
      // Loeschbar nur, wenn kein Artikel mehr offen (1), ausgegeben (3) oder
      // vernichtet (5) ist - genau diese drei zaehlt die Bestandsrechnung.
      return Array.isArray(user.allocations) &&
        !user.allocations.some(a => a.state === 1 || a.state === 3 || a.state === 5);
    },
    openDelete(item) {
      this.deleting = item;
      this.deleteDialog = true;
    },
    closeDelete() {
      if (this.deleteSaving) return;
      this.deleteDialog = false;
      this.deleting = null;
    },
    async confirmDelete() {
      if (!this.deleting || this.deleteSaving) return;
      this.deleteSaving = true;
      try {
        await this.$store.dispatch("update", {
          payload_type: "DELETE",
          context: "/shop/persons/" + this.deleting.id,
        });
      } finally {
        this.deleteSaving = false;
        this.deleteDialog = false;
        this.deleting = null;
      }
    },
  },
  computed: {
    emailDuplicate() {
      if (!this.editing) return false;
      const email = (this.editForm.email || "").trim().toLowerCase();
      if (!email) return false;
      const list = this.$store.state.shop.allocations || [];
      return list.some(
        (p) =>
          p.id !== this.editing.id &&
          (p.email || "").toLowerCase() === email
      );
    },
    canSave() {
      if (!this.editing || this.saving) return false;
      if (!this.editValid) return false;
      if (this.emailDuplicate) return false;
      const newLastName = (this.editForm.last_name || "").trim();
      const newEmail = (this.editForm.email || "").trim();
      const nameChanged = newLastName !== (this.editing.last_name || "");
      const emailChanged = newEmail !== (this.editing.email || "");
      return nameChanged || emailChanged;
    },
    variantMap() {
      const map = {};

      (this.$store.state.shop.products || []).forEach(product => {
        product.variants.forEach(variant => {
          map[variant.id] = {
            variant,
            product
          };
        });
      });

      return map;
    },
    allocations() {
      let list = this.$store.state.shop.allocations || [];
      list = list.filter(aloc => aloc.allocations.length > 0)
      let searchQuery = this.$store.state.general.global_search
      list = list.filter((usr) => (usr.last_name || "").toLowerCase().includes(searchQuery.toLowerCase()) || (usr.email || "").toLowerCase().includes(searchQuery.toLowerCase()))
        .map(user => {
            const active = user.allocations
              .filter(a => a.state === 3)
              .reduce((sum, a) => sum + (a.amount || 0), 0);
          return {
            ...user,
            active_count: active,
          };
        });
      return [...list].sort((a, b) => {
        const aOpen = this.hasOpenAllocations(a) ? 1 : 0;
        const bOpen = this.hasOpenAllocations(b) ? 1 : 0;
        // Offen (1) soll oben stehen
        return bOpen - aOpen;
      });
    },
    is_loading(){
      return this.$store.state.shop.products == null || this.$store.state.shop.allocations == null
    },
  },
  mounted() {
    this.updateAllocations();
  }
};
</script>

<style scoped></style>
